import React, { useLayoutEffect } from 'react';
import { useAsyncValue } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import AnimalsForm from '../../../../../components/forms/ActionForms/AnimalsForm';
import { Typography } from '../../../../../components/display/Typography';

// Constants
import { formActionValidationNaming } from '../../../../../components/forms/ActionForms/constants';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';

// Auth
import { useAuth } from '../../../../../hooks/useAuth';
import { Role } from '../../../../../types/auth';
import { OrderState } from '../../../../../types/orders';

// Envío del paso terminal (compartido con Materials, que es el terminal de la
// actuación básica desde 2026-08-11).
import { useActionValidationSubmit } from '../useActionValidationSubmit';

export default function AnimalsValidationFormPage(): JSX.Element {
  const { t } = useTranslation('validation');
  const dispatch = useDispatch();
  const { currentRole } = useAuth();

  const loaderData = useAsyncValue() as any;
  const userRole = currentRole as Role | null;

  // RoomTeam can only view, not edit/validate
  const isReadOnly = userRole === Role.RoomTeam;

  // Check if action is in partial state (cannot validate, only save)
  const isPartial = loaderData?.state === 'partial';

  // Una básica NO debería llegar aquí (su pestaña está oculta y el rebote de
  // `ActionPage` la manda a Materials), pero el flag se sigue propagando: si
  // alguien entra por un deep-link antes de que el rebote actúe, el envío debe
  // comportarse igual.
  const isBasicAction = !!loaderData?.isBasicAction;

  const { onFormSubmit, error, setIntent } = useActionValidationSubmit({
    isBasicAction,
    isReadOnly,
    userRole,
    terminalTab: 'animals',
  });

  useLayoutEffect(() => {
    dispatch(
      roidOTSelected('actions-validation', loaderData.roid, loaderData.orderId)
    );
  }, []);

  return (
    <>
      <AnimalsForm
        onFormSubmit={isReadOnly ? () => {} : onFormSubmit}
        initialData={loaderData}
        sessionStorageKey={
          formActionValidationNaming.NEW_VALIDATION_ANIMALS_FORM
        }
        updateKey={formActionValidationNaming.DATA_CHANGES}
        textButton={isReadOnly ? t('viewOnly') : userRole === Role.Tablet ? t('buttons:continue') : isPartial ? t('common:buttons.save') : t('validate')}
        userRole={userRole || undefined}
        showSaveButton={!isReadOnly && userRole !== Role.Tablet && !isPartial}
        saveButtonText={t('common:buttons.save')}
        onBeforeSave={() => setIntent('save')}
        onBeforeValidate={() => setIntent(isPartial ? 'save' : 'validate')}
        // Tarea #7 — Ocultar "Validar" cuando la actuación ya está en state=validated
        // (igual que en el summary, evitar ofrecer una transición al mismo estado).
        // Se mantiene visible en Tablet/readOnly/isPartial porque allí el botón
        // actúa como Continuar/Guardar, no como Validar.
        showValidateButton={
          isReadOnly ||
          userRole === Role.Tablet ||
          isPartial ||
          loaderData.state !== OrderState.Validated
        }
        // Tarea #7 — Pendent de certificar: mismas condiciones RBAC que Validar,
        // pero además se oculta si la actuación ya está en pending-certification.
        showPendingCertificationButton={
          !isReadOnly &&
          userRole !== Role.Tablet &&
          !isPartial &&
          loaderData.state !== OrderState.PendingCertification
        }
        pendingCertificationButtonText={t('validation:pendingCertification')}
        onBeforePendingCertification={() => setIntent('pending-certification')}
      />
      <Typography size="S" colorText={'red'}>
        {error}
      </Typography>
    </>
  );
}
